import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, startWith, map, debounceTime } from 'rxjs/operators';

// INTERFACES
import { OrderApiService } from '../../../shared/services/api/order/order-api.service';

import { OrderSalesInterface } from './../../../order/interfaces';
import { OptionInterface } from './../../../shared/interfaces/common.interface';

// SERVICES

@Component({
  selector: 'lum-filter-order',
  templateUrl: './filter-order.component.html',
  styleUrls: ['./filter-order.component.scss'],
})
export class FilterOrderComponent implements OnInit, OnDestroy {
  @Input()
  queryId :string = 'order';

  @Input()
  value :number[] = [];

  @Input()
  label :string = 'Sales Order';

  @Input()
  placeholder :string = '';

  @Input()
  multiple :boolean = false;

  @Input()
  inlineChips :boolean = false;

  @Input()
  withoutPicklist :boolean = false;

  @Input()
  excludeDeleted :boolean = true;

  @Output()
  onFilterSelect :EventEmitter<any[] | any> = new EventEmitter<any>();

  @ViewChild('filterInput')
  filterInput :ElementRef<HTMLInputElement>;

  orders :OptionInterface[];
  orders$ :Observable<OrderSalesInterface[]>;
  selectedOrders :OptionInterface[] = [];
  separatorKeysCodes :number[] = [ENTER, COMMA];

  inputControl = new UntypedFormControl();

  private onDestroy$ = new Subject();

  constructor(private orderService :OrderApiService) {}

  ngOnInit() :void {
    this.getFromService();
    this.getItemsFromObservable().subscribe();
    this.listenToInputControlChanges();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onAdd(option :OptionInterface) :void {
    this.selectedOrders.push(option);
    this.filterInput.nativeElement.value = '';
    this.inputControl.setValue(null);

    this.onFilterSelect.emit({
      [this.queryId]: this.selectedOrders.map(
        (option :OptionInterface) => option.value,
      ),
    });
  }

  onRemove(id :number) :void {
    this.selectedOrders = this.selectedOrders.filter(
      (option :OptionInterface) => option.value !== id,
    );
  }

  private getFromService() :void {
    this.orders$ = this.orderService.orders$;
  }

  private getItemsFromObservable() :Observable<OrderSalesInterface[]> {
    return this.orders$.pipe(
      tap((orders :OrderSalesInterface[]) => {
        this.orders = orders.map((row :OrderSalesInterface) => {
          return {
            value: row.id,
            label: row.orderNumber,
            count: row?.orderItems?.length ?? 0,
          };
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private listenToInputControlChanges() :void {
    this.inputControl.valueChanges
      .pipe(
        debounceTime(500),
        startWith(null),
        map((value :string) => {
          this.orderService.getOrders({
            columns: 'id,orderNumber',
            withoutPicklist: this.withoutPicklist.toString(),
            excludeDeleted: this.excludeDeleted.toString(),
            sortDirection: 'desc',
            orderNumber: value,
            relations: 'orderItems',
          });
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();
  }

  get uniqueOrders() :OptionInterface[] {
    return this.orders.filter(
      (option :OptionInterface) =>
        !this.selectedOrders.find(
          (selectedOption :OptionInterface) =>
            selectedOption.value === option.value,
        ),
    );
  }
}
